import * as React from 'react'
import EditVendorContactDialog from 'vendors/components/vendorContact/dialogs/EditVendorContactDialog'
import UserContactForm from 'vendors/components/vendorContact/dialogs/UserContactForm'
import { useUserContactForm } from 'vendors/components/vendorContact/useUserContactForm'
import { useGetUserContact, useUpdateUserContact } from 'vendors/queries/useVendorQuery'
import { useSnackbar } from 'snackbar/SnackbarContext'
import { useTranslation } from 'react-i18next'

type EditVendorUserContactDialogProps = {
  onClose: () => void
  vendorId: string
  userContactId: string
}

const EditVendorUserContactDialog: React.FC<EditVendorUserContactDialogProps> = ({
  onClose,
  vendorId,
  userContactId
}) => {
  const { isLoading, isSuccess, data: userContact } = useGetUserContact(vendorId, userContactId)
  const { vendorContactFormProps, vendorContactValues, email, initForm } = useUserContactForm()
  const { showSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const {
    isPending: isEditingUserContact,
    isSuccess: isEditingUserContactSuccess,
    mutate: editUserContact
  } = useUpdateUserContact(vendorId, userContactId)

  const isDisabled = React.useMemo(
    () => vendorContactValues.firstName === '' || vendorContactValues.lastName === '' || email === '',
    [email, vendorContactValues]
  )

  React.useEffect(() => {
    if (isSuccess && userContact) {
      const formData = {
        email: userContact.email,
        firstName: userContact.firstName,
        lastName: userContact.lastName,
        phone: userContact.phone,
        responsibilityDescription: userContact.responsibilityDescription,
        receiveRequestForProposals: userContact.receiveRequestForProposals
      }
      initForm(formData)
    }
  }, [isSuccess, userContact, initForm])

  React.useEffect(() => {
    if (isEditingUserContactSuccess) {
      showSnackbar(t('contact-edited-successfully'))
      onClose()
    }
  }, [isEditingUserContactSuccess, onClose, showSnackbar, t])

  const handleEditUserContact = () => {
    const payload: UpdateUserContactRequestType = {
      firstName: vendorContactValues.firstName,
      lastName: vendorContactValues.lastName,
      phone: vendorContactValues.phone,
      responsibilityDescription: vendorContactValues.responsibilityDescription,
      receiveRequestForProposals: vendorContactValues.receiveRequestForProposals,
      isDecisionMaker: userContact?.isDecisionMaker
    }

    editUserContact(payload)
  }

  return (
    <EditVendorContactDialog
      isInitingData={isLoading}
      onClose={onClose}
      isDisabled={isDisabled}
      onClick={handleEditUserContact}
      isLoading={isEditingUserContact}
    >
      <UserContactForm isEdit={true} {...vendorContactFormProps} />
    </EditVendorContactDialog>
  )
}

export default EditVendorUserContactDialog
